import React, { useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { GridToolbar } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SizeFlavorEditDialog from "./SizeFlavorEditDialog";

export default function CatalogSizeVariantGrid(props) {
  const { catalog, productType, variantType, sizeChart, options } = props;
  const [open, setOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);

  const handleCellClick = (sizeData, optionData) => {
    const cellData = getCellData(sizeData, optionData);

    setSelectedCell({ value: cellData, sizeData, optionData });
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setSelectedCell(null);
  };

  const handleSubmit = (data) => {
    console.log("data", data);
    let o = { ...catalog };
    for (const cat in o) {
      console.log("cat", cat, productType);
      if (cat === productType) {
        for (const variant in o[cat].variants) {
          console.log("variant", variant, variantType);
          if (variant === variantType) {
            console.log("variant", o[cat].variants[variant].field.options);
            for (const option of o[cat].variants[variant].field.options) {
              console.log("option", option, data.variantOption);

              if (option.name === data.variantOption) {
                if (data.override && Object.keys(data.override).length > 0) {
                  option.override = data.override;
                } else {
                  delete option.override;
                }

                /*if(option.override) {
                  if(data.override.size){
                    option.override.size = { ...option.override.size, ...data.override.size };
                  }
                } else {
                  option.override = data.override;
                }*/
              }
            }
          }
        }
      }
    }

    props.handleSubmit(o);
    onClose();
  };

  // Create columns for DataGrid
  const createColumns = () => {
    const columns = [
      {
        field: "name",
        headerName: "Size",
        width: 200,
        flex: 0, // Disable flex to use fixed width
        renderHeader: () => (
          <Typography
            variant="caption"
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.2,
              textAlign: "left",
              fontSize: "0.7rem"
            }}
          >
            Size
          </Typography>
        ),
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.2,
              padding: "4px",
              fontSize: "0.75rem"
            }}
          >
            {params.value}
          </Typography>
        )
      }
    ];

    // Sort options alphabetically by name
    const sortedOptions = [...options].sort((a, b) => a.name.localeCompare(b.name));

    sortedOptions.forEach((o, index) => {
      columns.push({
        field: `opt_${index}`,
        headerName: o.name,
        width: 100, // Fixed 100px width for all flavor columns
        flex: 0, // Disable flex to use fixed width
        renderHeader: () => (
          <Typography
            variant="caption"
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.2,
              textAlign: "left",
              fontSize: "0.7rem"
            }}
          >
            {o.name}
          </Typography>
        ),
        renderCell: (params) => {
          const sizeData = params.row;
          const optionData = o;

          const cellData = getCellData(sizeData, optionData);

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                justifyContent: "center",
                cursor: "pointer",
                height: "100%",
                padding: "4px"
              }}
              onClick={() => handleCellClick(sizeData, optionData)}
            >
              {String(cellData.allow) === "true" ? (
                <>
                  <Chip size="small" icon={<CheckCircleIcon color={cellData.hasOverride ? "warning" : "success"} />} label={cellData.pricing} />
                </>
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
          );
        }
      });
    });

    return columns;
  };

  // Get information about a specific size/option combination
  const getCellData = (sizeData, optionData) => {
    const result = {
      allow: true,
      pricing: null,
      hasOverride: false
    };

    // Check base pricing
    if (optionData.pricing) {
      if (optionData.pricing.amount) {
        result.pricing = `$${optionData.pricing.amount}`;
      } else if (optionData.pricing.factor) {
        result.pricing = `${optionData.pricing.factor * 100}%`;
      }
    }

    // Check if option has override pricing for this size
    if (optionData.override && optionData.override.size) {
      const sizeKey = sizeData.name;
      const overrideData = optionData.override.size[sizeKey];
      //console.log('>>>>>>>> OVR', sizeKey, flavorData.override.size);

      if (overrideData) {
        result.hasOverride = true;

        if (String(overrideData.allow) === "false") {
          result.allow = false;
        }

        if (overrideData.amount) {
          result.pricing = `$${overrideData.amount}`;
        }
      }
    }

    return result;
  };

  // Create rows for DataGrid
  const createRows = () => {
    const rows = sizeChart.map((size, index) => {
      const rowData = {
        id: size.id || index,
        name: size.name,
        ...size
      };

      // Add formatted data for each option column for export
      const sortedOptions = [...options].sort((a, b) => a.name.localeCompare(b.name));
      sortedOptions.forEach((option, optionIndex) => {
        const cellData = getCellData(size, option);
        const fieldName = `opt_${optionIndex}`;

        // Store the formatted value that matches what's displayed
        if (String(cellData.allow) === "true") {
          rowData[fieldName] = cellData.pricing || "Free";
        } else {
          rowData[fieldName] = "X";
        }
      });

      return rowData;
    });
    return rows;
  };

  if (!sizeChart || !options || sizeChart.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          No size chart or options data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          height: Math.min(400, sizeChart.length * 50 + 100),
          width: "100%",
          mb: 2,
          overflowX: "auto"
        }}
      >
        <DataGrid
          rows={createRows()}
          columns={createColumns()}
          hideFooter
          disableSelectionOnClick
          getRowHeight={() => 60}
          slots={{
            toolbar: GridToolbar
          }}
          sx={{
            backgroundColor: "#fff",
            minWidth: (options?.length || 0) * 100 + 200, // Ensure minimum width for all columns (100px each + 200px for size column)
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "flex-start",
              paddingTop: "8px",
              paddingBottom: "8px"
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5"
            },
            "& .MuiDataGrid-row": {
              minHeight: "60px !important"
            }
          }}
        />
      </Box>
      <SizeFlavorEditDialog open={open} onClose={onClose} onSubmit={handleSubmit} selectedCell={selectedCell} />
    </Box>
  );
}
